import { type FormEvent, type KeyboardEvent, useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { useDimenticata, useRipristina } from '../api/credenziali.js';
import { useAccesso } from '../api/sessione.js';
import { CorniceIngresso } from '../componenti/CorniceIngresso.js';
import { t } from '../i18n/lingua.js';

/**
 * Accesso, e le due pagine che ci stanno intorno.
 *
 * Nel v2 la lingua dell'applicazione seguiva l'azienda scelta al login:
 * qui l'azienda si sceglie dopo, e la lingua è una preferenza della persona.
 * Chi lavora su due società non deve più cambiare lingua per cambiare
 * azienda.
 *
 * «Password dimenticata» e il ripristino stanno qui e non fra le rotte: sono
 * le uniche due schermate che si vedono **senza** essere entrati, e il
 * router della 3.0 comincia dopo. Il link che arriva per posta è
 * `/reset-password?token=…`, e lo si riconosce dall'indirizzo.
 */

type Schermata = 'accesso' | 'dimenticata' | 'ripristino';

/** Il gettone nel link, se siamo arrivati da lì. */
function gettoneDallIndirizzo(): string | null {
  if (typeof window === 'undefined') return null;
  if (!window.location.pathname.startsWith('/reset-password')) return null;
  return new URLSearchParams(window.location.search).get('token');
}

export function Accesso() {
  const gettone = gettoneDallIndirizzo();
  const [schermata, setSchermata] = useState<Schermata>(gettone ? 'ripristino' : 'accesso');

  return (
    <CorniceIngresso>
      {/* la chiave rifà comparire la schermata quando cambia */}
      <div className="ingresso-schermata" key={schermata}>
        {schermata === 'accesso' && <ModuloAccesso onDimenticata={() => setSchermata('dimenticata')} />}
        {schermata === 'dimenticata' && (
          <ModuloDimenticata onIndietro={() => setSchermata('accesso')} />
        )}
        {schermata === 'ripristino' && <ModuloRipristino gettone={gettone} />}
      </div>
    </CorniceIngresso>
  );
}

function Intestazione({ titolo, sotto }: { titolo: string; sotto?: string }) {
  return (
    <header className="ingresso-intesta">
      <h1 className="ingresso-titolo">{titolo}</h1>
      {sotto && <p className="ingresso-sotto">{sotto}</p>}
    </header>
  );
}

function Errore({ messaggio }: { messaggio: string }) {
  return (
    <div className="avviso" role="alert">
      <span className="avviso-segno">▲</span>
      <span>{messaggio}</span>
    </div>
  );
}

/** Un esito andato bene: in verde, non nel rosso degli errori. */
function Esito({ messaggio }: { messaggio: string }) {
  return (
    <div className="avviso avviso-ok" role="status">
      <span className="avviso-segno">✓</span>
      <span>{messaggio}</span>
    </div>
  );
}

/**
 * Una password, con l'occhio per vederla mentre la si scrive.
 *
 * L'occhio serve soprattutto sul telefono, dove un carattere sbagliato non
 * si vede e dopo qualche tentativo scatta il freno. Per la stessa ragione
 * avvisa se le maiuscole sono bloccate: è la prima causa di «password
 * sbagliata» quando la password è giusta.
 */
function CampoSegreto({
  id,
  valore,
  cambia,
  autoComplete,
  autoFocus,
  minLength,
}: {
  id: string;
  valore: string;
  cambia: (valore: string) => void;
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
  minLength?: number;
}) {
  const [visibile, setVisibile] = useState(false);
  const [maiuscole, setMaiuscole] = useState(false);

  const leggiMaiuscole = (e: KeyboardEvent<HTMLInputElement>) =>
    setMaiuscole(e.getModifierState('CapsLock'));

  return (
    <>
      <div className="segreto">
        <input
          className="input"
          id={id}
          type={visibile ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          autoFocus={autoFocus}
          minLength={minLength}
          value={valore}
          onChange={(e) => cambia(e.target.value)}
          onKeyDown={leggiMaiuscole}
          onKeyUp={leggiMaiuscole}
          onBlur={() => setMaiuscole(false)}
          required
        />
        <button
          className="segreto-occhio"
          type="button"
          aria-label={t('Mostra la password')}
          aria-pressed={visibile}
          aria-controls={id}
          title={visibile ? t('Nascondi la password') : t('Mostra la password')}
          onClick={() => setVisibile((v) => !v)}
        >
          <Occhio chiuso={visibile} />
        </button>
      </div>
      {maiuscole && <span className="hint segreto-maiuscole">{t('Le maiuscole sono bloccate.')}</span>}
    </>
  );
}

/** Aperto: si può guardare. Barrato: la si sta già guardando. */
function Occhio({ chiuso }: { chiuso: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
      {chiuso && <path d="M4 20 20 4" />}
    </svg>
  );
}

function ModuloAccesso({ onDimenticata }: { onDimenticata: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /**
   * «Ricordami», come nel v2: spuntato, quel browser resta dentro trenta
   * giorni; altrimenti la sessione dura una giornata di lavoro, otto ore.
   */
  const [ricordami, setRicordami] = useState(false);
  const accesso = useAccesso();

  const errore = accesso.error instanceof ErroreApi ? accesso.error : null;

  const invia = (e: FormEvent) => {
    e.preventDefault();
    accesso.mutate({ email: email.trim(), password, remember: ricordami });
  };

  return (
    <>
      <Intestazione titolo={t('Accedi al CRM')} sotto={t('Inserisci e-mail e password per continuare.')} />

      <form onSubmit={invia}>
        {errore && <Errore messaggio={errore.message} />}

        <div className="field">
          <label htmlFor="email">{t('Indirizzo e-mail')}</label>
          <input
            className="input"
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {/* «Password dimenticata?» si vede accanto all'etichetta ma nel
            codice viene dopo il campo: col tasto Tab dall'e-mail si passa
            alla password, non al link */}
        <div className="field campo-segreto">
          <label htmlFor="password">{t('Password')}</label>
          <CampoSegreto
            id="password"
            valore={password}
            cambia={setPassword}
            autoComplete="current-password"
          />
          <button className="ingresso-link campo-dimenticata" type="button" onClick={onDimenticata}>
            {t('Password dimenticata?')}
          </button>
        </div>

        <label className="check" htmlFor="ricordami">
          <input
            id="ricordami"
            type="checkbox"
            checked={ricordami}
            onChange={(e) => setRicordami(e.target.checked)}
          />
          <span>{t('Ricordami per 30 giorni su questo computer')}</span>
        </label>

        <button className="btn btn-primary" type="submit" disabled={accesso.isPending}>
          {accesso.isPending ? t('Un momento…') : t('Entra')}
        </button>
      </form>

      <p className="ingresso-nota">
        {t('Si entra con le stesse credenziali di oggi: al passaggio alla 3.0 nessuno deve reimpostare la password.')}
      </p>
    </>
  );
}

/**
 * «Password dimenticata?».
 *
 * La risposta è la stessa che l'indirizzo esista o no — il v2 dice «E-Mail
 * non presente nel sistema», e su una pagina pubblica è un modo per scoprire
 * chi ha un'utenza.
 */
function ModuloDimenticata({ onIndietro }: { onIndietro: () => void }) {
  const [email, setEmail] = useState('');
  const richiesta = useDimenticata();

  const errore = richiesta.error instanceof ErroreApi ? richiesta.error : null;

  const indietro = (
    <p className="ingresso-indietro">
      <button className="ingresso-link" type="button" onClick={onIndietro}>
        {t('← Torna all’accesso')}
      </button>
    </p>
  );

  if (richiesta.data) {
    return (
      <>
        <Intestazione titolo={t('Controlla la posta')} />
        <Esito messaggio={richiesta.data.message} />
        {/* solo quando la posta non è configurata: in produzione non c'è */}
        {richiesta.data.devLink && (
          <p className="ingresso-nota">
            {t('La posta non è configurata, quindi il messaggio non è partito. Il link è questo:')}{' '}
            <a href={richiesta.data.devLink}>{richiesta.data.devLink}</a>
          </p>
        )}
        {indietro}
      </>
    );
  }

  return (
    <>
      <Intestazione
        titolo={t('Password dimenticata?')}
        sotto={t('Scrivi l’indirizzo con cui entri: ti arriva un messaggio con il link per rifare la password.')}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          richiesta.mutate(email.trim());
        }}
      >
        {errore && <Errore messaggio={errore.message} />}

        <div className="field">
          <label htmlFor="email-recupero">{t('Indirizzo e-mail')}</label>
          <input
            className="input"
            id="email-recupero"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <button className="btn btn-primary" type="submit" disabled={richiesta.isPending}>
          {richiesta.isPending ? t('Un momento…') : t('Mandami il link')}
        </button>
      </form>

      {indietro}
    </>
  );
}

/**
 * La password nuova, dal link ricevuto per posta.
 *
 * Il v2 qui, se il gettone non si ritrova, non dice niente: si finisce sulla
 * pagina di accesso senza una parola. E siccome il gettone del v2 è in
 * base64 — con dentro `+` e `/`, che in un indirizzo non sopravvivono — è
 * quello che succede quasi sempre.
 */
function ModuloRipristino({ gettone }: { gettone: string | null }) {
  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const ripristina = useRipristina();

  const errore = ripristina.error instanceof ErroreApi ? ripristina.error : null;
  const diverse = conferma.length > 0 && password !== conferma;

  if (!gettone) {
    return (
      <>
        <Intestazione titolo={t('Link incompleto')} />
        <Errore messaggio={t('Questo link non è completo: rifai la richiesta di ripristino.')} />
        <a className="btn btn-primary" href="/">
          {t('Vai all’accesso')}
        </a>
      </>
    );
  }

  if (ripristina.data) {
    return (
      <>
        <Intestazione titolo={t('Password cambiata')} />
        <Esito messaggio={t('La password è cambiata. Adesso puoi entrare.')} />
        <a className="btn btn-primary" href="/">
          {t('Vai all’accesso')}
        </a>
      </>
    );
  }

  return (
    <>
      <Intestazione titolo={t('Scegli la password nuova')} sotto={t('Almeno otto caratteri.')} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!diverse) ripristina.mutate({ token: gettone, newPassword: password });
        }}
      >
        {errore && <Errore messaggio={errore.message} />}

        <div className="field campo-segreto">
          <label htmlFor="pw-nuova">{t('Password nuova')}</label>
          <CampoSegreto
            id="pw-nuova"
            valore={password}
            cambia={setPassword}
            autoComplete="new-password"
            autoFocus
            minLength={8}
          />
        </div>

        <div className="field campo-segreto">
          <label htmlFor="pw-conferma">{t('Ripetila')}</label>
          <CampoSegreto
            id="pw-conferma"
            valore={conferma}
            cambia={setConferma}
            autoComplete="new-password"
          />
          {diverse && (
            <span className="hint" style={{ color: 'var(--tomato)' }}>
              {t('Le due password non coincidono.')}
            </span>
          )}
        </div>

        <button
          className="btn btn-primary"
          type="submit"
          disabled={ripristina.isPending || diverse || password.length < 8}
        >
          {ripristina.isPending ? t('Un momento…') : t('Cambia la password')}
        </button>
      </form>
    </>
  );
}
